import React, { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Plus, Download, ExternalLink, Trash2, Globe, X, Info, FileText, Image as ImageIcon, FileSpreadsheet, File, Video, PlayCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  getAccountById,
  updateAccount,
  getGlobalDocuments,
  addGlobalDocument,
  deleteGlobalDocument
} from '../services/firestoreService';
import { fetchAccountCalls } from '../services/departmentCallsService';
import MeetingSummaryContent from '../components/MeetingSummaryContent';
import MeetingSummaryActionItems from '../components/MeetingSummaryActionItems';
import PortalBackground from '../components/PortalBackground';

const DOCUMENT_DEPARTMENTS = ['General', 'Sales', 'Operations', 'Technology', 'Finance', 'Marketing', 'HR'];

// Pick a file-type icon + accent color from the document name/url.
function getFileMeta(docItem) {
  const source = docItem?.fileName || docItem?.name || docItem?.url || '';
  const ext = (source.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return { Icon: ImageIcon, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/40' };
  }
  if (['csv', 'xls', 'xlsx'].includes(ext)) {
    return { Icon: FileSpreadsheet, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/40' };
  }
  if (ext === 'pdf') {
    return { Icon: FileText, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/40' };
  }
  if (['doc', 'docx'].includes(ext)) {
    return { Icon: FileText, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/40' };
  }
  return { Icon: File, color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-700' };
}

const DocumentCollection = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [loading, setLoading] = useState(true);
  const [accountId, setAccountId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [activeScope, setActiveScope] = useState('mine'); // 'calls' | 'mine' | 'global'

  const [customDocuments, setCustomDocuments] = useState([]);
  const [globalDocuments, setGlobalDocuments] = useState([]);
  const [myCalls, setMyCalls] = useState([]);
  const [activeCall, setActiveCall] = useState(null); // call whose summary modal is open

  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingUploadDepartment, setPendingUploadDepartment] = useState('General');
  const [pendingUploadTitle, setPendingUploadTitle] = useState('');
  const [pendingUploadDescription, setPendingUploadDescription] = useState('');
  const [pendingUploadScope, setPendingUploadScope] = useState('mine'); // 'mine' | 'global'

  const [descriptionDoc, setDescriptionDoc] = useState(null); // doc whose description popup is open

  // Fetch account, internal role, custom documents, and the global library.
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Internal-user detection (same pattern as ClientOverview).
        let internal = false;
        try {
          const internalSnap = await getDoc(doc(db, 'internal_users', currentUser.uid));
          internal = internalSnap.exists();
        } catch (e) {
          internal = false;
        }
        setIsInternal(internal);

        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        const userData = userDoc.exists() ? userDoc.data() : {};
        // The account this login is linked to (clients may only read their own)
        const accId = userData.accountId || userData.clientId;

        if (accId) {
          setAccountId(accId);
          const accountData = await getAccountById(accId);
          setCustomDocuments(accountData?.customDocuments || []);
          setCompanyName(accountData?.name || accountData?.contact?.name || '');

          // Calls: booked strategy calls + completed Fathom recordings/summaries.
          try {
            const calls = await fetchAccountCalls(accId);
            setMyCalls(calls);
          } catch (e) {
            console.error('Error loading calls:', e);
            setMyCalls([]);
          }
        } else {
          setCustomDocuments([]);
          setMyCalls([]);
        }

        // Global library (visible to everyone).
        try {
          const globals = await getGlobalDocuments();
          setGlobalDocuments(globals);
        } catch (e) {
          setGlobalDocuments([]);
        }

        // Internal users without a linked account land on the Global tab.
        if (!accId && internal) {
          setActiveScope('global');
        }
      } catch (error) {
        console.error('Error loading documents:', error);
        setCustomDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // My Documents: client-facing docs for this account.
  const myDocuments = useMemo(
    () => customDocuments.filter((d) => d.visibility === 'client' || !d.visibility),
    [customDocuments]
  );

  const visibleDocuments = useMemo(() => {
    const source = activeScope === 'global' ? globalDocuments : myDocuments;
    const q = searchQuery.toLowerCase();
    return source.filter((d) => {
      const matchesSearch =
        d.name?.toLowerCase().includes(q) ||
        d.uploader?.toLowerCase().includes(q) ||
        d.uploaderCompany?.toLowerCase().includes(q);
      const matchesDepartment = selectedDepartment === 'All' || d.department === selectedDepartment;
      return matchesSearch && matchesDepartment;
    });
  }, [activeScope, globalDocuments, myDocuments, searchQuery, selectedDepartment]);

  const visibleCalls = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return myCalls.filter((call) => {
      const matchesSearch = !q || call.title?.toLowerCase().includes(q);
      const matchesDepartment =
        selectedDepartment === 'All' ||
        (call.department || '').toLowerCase() === selectedDepartment.toLowerCase();
      return matchesSearch && matchesDepartment;
    });
  }, [myCalls, searchQuery, selectedDepartment]);

  const formatCallDate = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleViewInNewTab = (docItem) => {
    if (docItem.url) window.open(docItem.url, '_blank', 'noopener,noreferrer');
  };

  const handleDocumentDownload = (docItem) => {
    if (!docItem.url) return;
    const link = document.createElement('a');
    link.href = docItem.url;
    link.download = docItem.name || 'document';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // A document can be deleted by its uploader; global docs can also be deleted by internal staff.
  const canDeleteDocument = (docItem, scope) => {
    if (docItem.uploaderUserId && docItem.uploaderUserId === currentUser?.uid) return true;
    if (scope === 'global' && isInternal) return true;
    return false;
  };

  const handleMyDocumentDelete = async (documentId) => {
    const docToDelete = myDocuments.find((d) => d.id === documentId);
    if (!docToDelete || !canDeleteDocument(docToDelete, 'mine')) return;
    if (!window.confirm('Delete this document?')) return;

    try {
      if (docToDelete.fileName) {
        await deleteObject(ref(storage, `client-documents/${docToDelete.fileName}`));
      }
      const updatedDocuments = customDocuments.filter((d) => d.id !== documentId);
      // Only the list changes; writing the whole doc back would replay stale fields
      await updateAccount(accountId, { customDocuments: updatedDocuments });
      setCustomDocuments(updatedDocuments);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const handleGlobalDocumentDelete = async (documentId) => {
    const docToDelete = globalDocuments.find((d) => d.id === documentId);
    if (!docToDelete || !canDeleteDocument(docToDelete, 'global')) return;
    if (!window.confirm('Delete this document from the Global library? Everyone will lose access.')) return;

    try {
      if (docToDelete.fileName) {
        await deleteObject(ref(storage, `global-documents/${docToDelete.fileName}`)).catch(() => {});
      }
      await deleteGlobalDocument(documentId);
      setGlobalDocuments((prev) => prev.filter((d) => d.id !== documentId));
    } catch (error) {
      console.error('Error deleting global document:', error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const handleDocumentDelete = (docItem) => {
    if (activeScope === 'global') handleGlobalDocumentDelete(docItem.id);
    else handleMyDocumentDelete(docItem.id);
  };

  const handleUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setPendingUploadFile(file);
        setPendingUploadDepartment('General');
        setPendingUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
        setPendingUploadDescription('');
        // Default the destination to the tab the user is on; clients with no account go global.
        // The calls tab is read-only, so uploads started from it go to My Documents.
        setPendingUploadScope(!accountId ? 'global' : activeScope === 'global' ? 'global' : 'mine');
        setShowUploadModal(true);
      }
    };
    input.click();
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setPendingUploadFile(null);
    setPendingUploadDepartment('General');
    setPendingUploadTitle('');
    setPendingUploadDescription('');
  };

  const resolveUploaderName = (accountData) => {
    const contactName =
      accountData?.contact?.name ||
      [accountData?.contact?.firstName, accountData?.contact?.lastName].filter(Boolean).join(' ').trim() ||
      null;
    return (
      contactName ||
      currentUser?.displayName ||
      (currentUser?.email
        ? currentUser.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
        : 'Unknown')
    );
  };

  const handleUploadSubmit = async () => {
    const file = pendingUploadFile;
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }
    if (pendingUploadScope === 'mine' && !accountId) {
      alert('No account linked, so this can only be posted to the Global library.');
      return;
    }

    try {
      setUploadingDocument(true);

      const accountData = accountId ? await getAccountById(accountId) : null;
      const uploaderName = resolveUploaderName(accountData);
      const timestamp = Date.now();
      const extMatch = file.name.match(/\.([^/.]+)$/);
      const fileExtension = extMatch ? `.${extMatch[1]}` : '';
      const documentName = (pendingUploadTitle || file.name.replace(/\.[^/.]+$/, '')).trim() || file.name;

      if (pendingUploadScope === 'global') {
        const fileName = `global-${timestamp}${fileExtension}`;
        const storageRef = ref(storage, `global-documents/${fileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const newDoc = {
          name: documentName,
          description: pendingUploadDescription.trim() || null,
          url: downloadURL,
          fileName,
          uploader: uploaderName,
          uploaderUserId: currentUser?.uid || null,
          uploaderType: isInternal ? 'internal' : 'client',
          uploaderCompany: companyName || null,
          department: pendingUploadDepartment || null,
          uploadDate: new Date().toISOString()
        };
        const newId = await addGlobalDocument(newDoc);
        setGlobalDocuments((prev) => [{ id: newId, ...newDoc }, ...prev]);
      } else {
        const fileName = `client-${accountId}-custom-${timestamp}${fileExtension}`;
        const storageRef = ref(storage, `client-documents/${fileName}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const newDoc = {
          id: timestamp.toString(),
          name: documentName,
          url: downloadURL,
          uploader: uploaderName,
          uploaderUserId: currentUser?.uid || null,
          uploadDate: new Date().toISOString(),
          fileName,
          visibility: 'client',
          department: pendingUploadDepartment || null
        };
        const updatedDocuments = [...customDocuments, newDoc];
        await updateAccount(accountId, { customDocuments: updatedDocuments });
        setCustomDocuments(updatedDocuments);
      }

      setActiveScope(pendingUploadScope);
      resetUploadModal();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploadingDocument(false);
    }
  };

  const departmentOptions = ['All', ...DOCUMENT_DEPARTMENTS];

  const scopeTab = (key, label, count) => {
    const active = activeScope === key;
    return (
      <button
        key={key}
        onClick={() => setActiveScope(key)}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
          active
            ? 'bg-red-600 text-white shadow-sm'
            : darkMode
            ? 'text-gray-300 hover:bg-gray-700'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        {key === 'global' && <Globe className="h-4 w-4" />}
        {key === 'calls' && <Video className="h-4 w-4" />}
        <span className="truncate max-w-[200px]">{label}</span>
        <span
          className={`text-xs px-1.5 py-0.5 rounded-full ${
            active ? 'bg-white/20 text-white' : darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-600'
          }`}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto px-4 py-6">
        {/* Header: centered title + add button */}
        <div className="relative mb-4">
          <h1 className={`text-2xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>Documents</h1>
          <button
            onClick={handleUploadClick}
            className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 px-3.5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-sm"
          >
            <Plus className="h-4 w-4" />
            Add Document
          </button>
        </div>

        {/* Scope tabs */}
        <div className={`flex flex-wrap justify-center gap-2 p-1 rounded-xl mb-4 mx-auto w-fit bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm`}>
          {scopeTab('calls', 'My Calls', myCalls.length)}
          {scopeTab('mine', companyName || 'My Documents', myDocuments.length)}
          {scopeTab('global', 'Global Documents', globalDocuments.length)}
        </div>

        {/* Search and department filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className={`block w-full pl-9 pr-3 py-2 text-sm border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
              }`}
              placeholder={activeScope === 'calls' ? 'Search calls...' : 'Search documents...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="relative sm:w-44">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-4 w-4 text-gray-400" />
            </div>
            <select
              className={`block w-full pl-9 pr-3 py-2 text-sm border rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
              }`}
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              {departmentOptions.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Document / call grid */}
        {loading ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">Loading...</div>
        ) : activeScope === 'calls' ? (
          !accountId ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              No account linked. Your calls will appear here once your account is set up.
            </div>
          ) : visibleCalls.length === 0 ? (
            <div
              className={`rounded-xl border border-dashed p-12 text-center ${
                darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
              }`}
            >
              <Video className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No calls found</p>
              <p className="text-sm mt-1">
                {searchQuery || selectedDepartment !== 'All'
                  ? 'Try adjusting your search or filter.'
                  : 'Your meeting summaries and recordings will appear here after your calls.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleCalls.map((call) => {
                const hasSummary = !!call.summary || (call.actionItems && call.actionItems.length > 0);
                return (
                  <div
                    key={call.id}
                    className={`group rounded-xl border p-4 transition-all hover:shadow-md ${
                      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-100 dark:bg-red-900/40">
                        <Video className="w-5 h-5 text-red-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} title={call.title}>
                          {call.title}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {[formatCallDate(call.date), call.status].filter(Boolean).join(' · ')}
                        </p>
                        {call.department && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 capitalize">
                              {call.department}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      {hasSummary ? (
                        <button
                          onClick={() => setActiveCall(call)}
                          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <FileText className="h-3.5 w-3.5" /> Meeting Summary
                        </button>
                      ) : (
                        <span className={`flex-1 text-center text-xs py-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          No summary yet
                        </span>
                      )}
                      {call.recordingUrl && (
                        <a
                          href={call.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                        >
                          <PlayCircle className="h-3.5 w-3.5" /> Recording
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : activeScope === 'mine' && !accountId ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            No account linked. Your documents will appear here once your account is set up.
          </div>
        ) : visibleDocuments.length === 0 ? (
          <div
            className={`rounded-xl border border-dashed p-12 text-center ${
              darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
            }`}
          >
            <File className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No documents found</p>
            <p className="text-sm mt-1">
              {searchQuery || selectedDepartment !== 'All'
                ? 'Try adjusting your search or filter.'
                : activeScope === 'global'
                ? 'Be the first to share a document with everyone.'
                : 'Upload a document to get started.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleDocuments.map((docItem) => {
              const { Icon, color, bg } = getFileMeta(docItem);
              const deletable = canDeleteDocument(docItem, activeScope);
              return (
                <div
                  key={docItem.id}
                  className={`group rounded-xl border p-4 transition-all hover:shadow-md ${
                    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                      <Icon className={`w-5 h-5 ${color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`} title={docItem.name}>
                        {docItem.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {docItem.uploader || 'Unknown'}
                        {activeScope === 'global' && docItem.uploaderCompany ? ` · ${docItem.uploaderCompany}` : ''}
                        {docItem.uploadDate ? ` · ${new Date(docItem.uploadDate).toLocaleDateString()}` : ''}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {activeScope === 'global' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            <Globe className="h-2.5 w-2.5" /> Global
                          </span>
                        )}
                        {docItem.department && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {docItem.department}
                          </span>
                        )}
                      </div>
                    </div>
                    {docItem.description && (
                      <button
                        onClick={() => setDescriptionDoc(docItem)}
                        title="View description"
                        className={`flex-shrink-0 -mt-0.5 -mr-0.5 p-1 rounded-md transition-colors ${
                          darkMode ? 'text-gray-500 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={() => handleViewInNewTab(docItem)}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        darkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> View
                    </button>
                    <button
                      onClick={() => handleDocumentDownload(docItem)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                    {deletable && (
                      <button
                        onClick={() => handleDocumentDelete(docItem)}
                        title="Delete"
                        className={`px-2.5 py-1.5 rounded-lg border transition-colors ${
                          darkMode
                            ? 'border-gray-600 text-gray-400 hover:bg-red-900/30 hover:text-red-400'
                            : 'border-gray-300 text-gray-400 hover:bg-red-50 hover:text-red-600'
                        }`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Meeting summary modal */}
      {activeCall && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setActiveCall(null)}
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-2xl w-full max-h-[85vh] flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`relative flex items-center justify-center p-5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="text-center px-8">
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {activeCall.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {[formatCallDate(activeCall.date), activeCall.status].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button
                onClick={() => setActiveCall(null)}
                className="absolute right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {activeCall.recordingUrl && (
                <a
                  href={activeCall.recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white"
                >
                  <PlayCircle className="w-3.5 h-3.5" />
                  Watch Recording
                </a>
              )}
              {activeCall.summary ? (
                <MeetingSummaryContent markdown={activeCall.summary} />
              ) : (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No summary is available for this call yet.
                </p>
              )}
              {activeCall.actionItems && activeCall.actionItems.length > 0 && (
                <MeetingSummaryActionItems actionItems={activeCall.actionItems} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Description popup */}
      {descriptionDoc && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setDescriptionDoc(null)}
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center justify-center mb-4">
              <h3 className={`text-base font-semibold text-center px-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {descriptionDoc.name}
              </h3>
              <button onClick={() => setDescriptionDoc(null)} className="absolute right-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {descriptionDoc.description}
            </p>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && pendingUploadFile && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-lg w-full`}>
            <div className="relative flex items-center justify-center mb-5">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Add Document</h3>
              <button onClick={resetUploadModal} className="absolute right-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className={`text-xs mb-4 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {pendingUploadFile.name}
            </p>

            <div className="space-y-4 mb-6">
              {/* Post to */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Post to</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={!accountId}
                    onClick={() => setPendingUploadScope('mine')}
                    className={`px-3 py-2 text-sm rounded-lg border text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      pendingUploadScope === 'mine'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium block">My Documents</span>
                    <span className="text-xs opacity-80">Private to {companyName || 'my company'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingUploadScope('global')}
                    className={`px-3 py-2 text-sm rounded-lg border text-left transition-colors ${
                      pendingUploadScope === 'global'
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> Global</span>
                    <span className="text-xs opacity-80">Visible to everyone</span>
                  </button>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Title</label>
                <input
                  type="text"
                  value={pendingUploadTitle}
                  onChange={(e) => setPendingUploadTitle(e.target.value)}
                  placeholder="Add a title or leave as filename"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Description (Global only) */}
              {pendingUploadScope === 'global' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                  <textarea
                    value={pendingUploadDescription}
                    onChange={(e) => setPendingUploadDescription(e.target.value)}
                    rows={3}
                    placeholder="Briefly describe this document for everyone"
                    className={`w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-red-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
              )}

              {/* Department */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Department</label>
                <select
                  value={pendingUploadDepartment}
                  onChange={(e) => setPendingUploadDepartment(e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  {DOCUMENT_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={resetUploadModal}
                className={`px-4 py-2 text-sm rounded-lg border ${
                  darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={uploadingDocument}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingDocument ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentCollection;
